export interface IImportedMapping {
  localPath: string;
  s3Prefix: string;
}

export interface IMappingImportResult {
  mappings: IImportedMapping[];
  duplicateCount: number;
  errors: string[];
}

function isAbsoluteWindowsPath(path: string): boolean {
  return /^[a-z]:[\\/]/i.test(path) || /^\\\\[^\\/]+[\\/][^\\/]+/.test(path);
}

function normalizeServerPath(path: string): string {
  const normalized = path
    .normalize('NFC')
    .replace(/\\/g, '/')
    .replace(/^\/+|\/+$/g, '')
    .replace(/\/{2,}/g, '/');

  return normalized === '' ? '' : `${normalized}/`;
}

export function mappingIdentity(mapping: IImportedMapping): string {
  return `${mapping.localPath.normalize('NFC').toLocaleLowerCase('de-DE')}\0${mapping.s3Prefix.normalize('NFC')}`;
}

/**
 * Parses HiDriveSync directory-list exports:
 *
 *   G:\local\folder  ->  Server\folder;G:\other  ->  Server\other
 *
 * Records may be separated by semicolons or line breaks. Server paths are
 * converted to the forward-slash, trailing-slash S3 prefixes used by Syncer.
 */
export function parseMappingImport(content: string): IMappingImportResult {
  const records = content
    .replace(/^\uFEFF/, '')
    .split(/[;\r\n]+/)
    .map((record) => record.trim())
    .filter(Boolean);

  const mappings: IImportedMapping[] = [];
  const seen = new Set<string>();
  const errors: string[] = [];
  let duplicateCount = 0;

  records.forEach((record, index) => {
    const separatorIndex = record.indexOf('->');
    if (separatorIndex < 0) {
      errors.push(`Eintrag ${index + 1}: Trennzeichen "->" fehlt.`);
      return;
    }

    const localPath = record.slice(0, separatorIndex).trim().normalize('NFC');
    const s3Prefix = normalizeServerPath(record.slice(separatorIndex + 2).trim());

    if (!isAbsoluteWindowsPath(localPath)) {
      errors.push(`Eintrag ${index + 1}: "${localPath || '(leer)'}" ist kein absoluter Windows-Pfad.`);
      return;
    }
    if (s3Prefix === '') {
      errors.push(`Eintrag ${index + 1}: Der Server-Ordner fehlt.`);
      return;
    }

    const serverSegments = s3Prefix.split('/').filter(Boolean);
    if (
      /^[a-z]:\//i.test(s3Prefix) ||
      serverSegments.some((segment) => segment === '.' || segment === '..')
    ) {
      errors.push(`Eintrag ${index + 1}: Der Server-Ordner "${s3Prefix}" ist ungültig.`);
      return;
    }

    const mapping = { localPath, s3Prefix };
    const identity = mappingIdentity(mapping);
    if (seen.has(identity)) {
      duplicateCount += 1;
      return;
    }

    seen.add(identity);
    mappings.push(mapping);
  });

  if (records.length === 0) {
    errors.push('Die Datei enthält keine Zuordnungen.');
  }

  return { mappings, duplicateCount, errors };
}
